import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import { getItem, logout } from "../../utils/authStorage";
import { parseJwt } from "../../utils/jwtDecoder";
import { fetchDroneRunwayAlerts, triggerForeignObjectDetection, fetchActiveDrones } from "../../services/deviceService";
import { toast } from 'react-toastify';
import { useAlertWebSocket } from "../../contexts/WebSocketContext";
import { useTheme } from "../../contexts/ThemeContext";
import { TablerSearch, TablerFlight, TablerCalender } from "../shared/Icons";
import LoadingSpinner from "../shared/LoadingSpinner";
import "./DroneRunway.css";
import CustomSelect from "../shared/CustomSelect";
import { SEVERITY_OPTIONS, DRONE_RUNWAY_STATUS_OPTIONS, ROWS_PER_PAGE_OPTIONS } from "../shared/selectOptions";
import { parseDateInput, formatDisplayDate } from "../../utils/dateUtils";

function DroneRunway() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLaunchingFlight, setIsLaunchingFlight] = useState(false);
  const [activeDrones, setActiveDrones] = useState([]);
  const [selectedDroneId, setSelectedDroneId] = useState("");
  const [activeFlight, setActiveFlight] = useState(null);

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [filterDate, setFilterDate] = useState("");
  const [dateInputFocused, setDateInputFocused] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { subscribe } = useAlertWebSocket();

  // User Profile Info
  const token = getItem("accessToken");
  const tokenPayload = token ? parseJwt(token) : null;
  const username = getItem("username") || tokenPayload?.username || tokenPayload?.preferred_username || "User";
  const rawRole = getItem("role") || tokenPayload?.role || tokenPayload?.roles?.[0] || "Operator";
  const role = rawRole.toLowerCase();

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  const handleSignOut = () => {
    logout();
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const data = await fetchDroneRunwayAlerts();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load drone runway alerts:", error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveFlight = async () => {
    const token = getItem("accessToken");
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    try {
      const res = await fetch(`${baseUrl}/command-center/cc-alerts/foreign-object-detection/active/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setActiveFlight(data.active ? data : null);
      }
    } catch {}
  };

  const handleGoLive = () => {
    if (!activeFlight) return;
    navigate(`/foreign-object-detection-stream/${activeFlight.flight_id}`, {
      state: { flightData: activeFlight }
    });
  };

  // WebSockets refresh
  useEffect(() => {
    const unsub = subscribe((raw) => {
      if (raw?.type === "foreign_object.detected") {
        loadAlerts();
      } else if (raw?.type === "drone_flight.video_annotated") {
        loadAlerts();
      } else if (raw?.type === "drone.streaming" || raw?.type === "drone.mission") {
        checkActiveFlight();
      }
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    loadAlerts();
    checkActiveFlight();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const drones = await fetchActiveDrones();
        setActiveDrones(Array.isArray(drones) ? drones : []);
      } catch (err) {
        console.error("Failed to load active drones:", err);
        setActiveDrones([]);
      }
    })();
  }, []);

  const handleDroneFly = async () => {
    if (!selectedDroneId) {
      toast.error(t("label.selectDroneFirst"));
      return;
    }
    setIsLaunchingFlight(true);
    try {
      const flightData = await triggerForeignObjectDetection(selectedDroneId);

      if (flightData?.flight_id) {
        navigate(`/foreign-object-detection-stream/${flightData.flight_id}`, {
          state: { flightData }
        });
        toast.success(t("label.droneFlightLaunched"));
      } else {
        toast.error(t("label.unableStartDroneFlight"));
      }
    } catch (err) {
      console.error('Drone Fly error:', err);
      toast.error(err?.message || t("label.droneFlightFailed"));
    } finally {
      setIsLaunchingFlight(false);
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setSeverityFilter("All");
    setStatusFilter("All");
    setFilterDate("");
    setDateInputFocused(false);
    setCurrentPage(1);
  };

  const truncateId = (id) => {
    if (!id) return "N/A";
    if (id.length <= 14) return id;
    return `${id.substring(0, 8)}..${id.substring(id.length - 4)}`;
  };

  const formatBadgeLabel = (text) => {
    if (!text) return "N/A";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hh = date.getHours();
    const min = String(date.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
  };

  // Filter Logic
  const filteredAlerts = useMemo(() => {
    return alerts
      .filter((alert) => {
        const alertId = alert.alertId || "";
        const matchesId =
          !searchQuery ||
          alertId.toLowerCase().includes(searchQuery.toLowerCase().trim());

        const severity = alert.severity || "";
        const matchesSeverity =
          severityFilter === "All" ||
          severity.toLowerCase() === severityFilter.toLowerCase();

        const status = alert.status || "";
        const matchesStatus =
          statusFilter === "All" ||
          status.toLowerCase() === statusFilter.toLowerCase();

        const matchesDate =
          !filterDate ||
          (() => {
            const selectedDate = parseDateInput(filterDate);
            if (!selectedDate || !alert.timestamp) return false;
            const alertDate = new Date(alert.timestamp);
            return (
              alertDate.getDate() === selectedDate.getDate() &&
              alertDate.getMonth() === selectedDate.getMonth() &&
              alertDate.getFullYear() === selectedDate.getFullYear()
            );
          })();

        return matchesId && matchesSeverity && matchesStatus && matchesDate;
      })
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [alerts, searchQuery, severityFilter, statusFilter, filterDate]);

  // Pagination Logic
  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / rowsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedAlerts = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAlerts.slice(start, start + rowsPerPage);
  }, [filteredAlerts, currentPage, rowsPerPage]);

  return (
    <div className={`drone-runway-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />

      <main className="drone-runway-main-content">
        <Header title={t("nav.droneRunway")} />
        <div className="drone-runway-divider" />

        <div className="drone-runway-body">
          {/* Section Header */}
          <div className="drone-runway-section-header">
            <div>
              <h2 className="drone-runway-title">{t("label.droneAlerts")}</h2>
              <p className="drone-runway-subtitle">{t("label.droneAlertsSubtitle")}</p>
            </div>
            <div className="drone-runway-fly-controls" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {activeFlight && sessionStorage.getItem('fodStreamActive') === 'true' && (
                <button className="drone-runway-go-live-btn" onClick={handleGoLive}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#22c55e" style={{ marginRight: 6, flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="8" />
                  </svg>
                  Go Live
                </button>
              )}
              <CustomSelect
                value={selectedDroneId}
                onChange={(val) => setSelectedDroneId(val)}
                options={[
                  { value: "", label: t("label.selectDrone") },
                  ...activeDrones.map((d) => ({ value: d.id, label: d.name })),
                ]}
                className="drone-runway-drone-select"
              />
              <button
                className="drone-runway-fly-btn"
                onClick={handleDroneFly}
                disabled={isLaunchingFlight || !selectedDroneId}
              >
                <TablerFlight style={{ marginRight: "8px" }} />
                {isLaunchingFlight ? t("button.launching") : t("button.inspectFod")}
              </button>
            </div>
          </div>

          {/* Filters Area */}
          <div className="drone-runway-filters-row">
            <div className="drone-runway-filter-inputs">
              
              {/* Search input */}
              <div className="drone-runway-filter-field">
                <div className="drone-runway-input-wrapper">
                  <TablerSearch className="drone-runway-input-icon" size={18} />
                  <input
                    type="text"
                    placeholder={t("placeholder.searchByAlertId")}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="drone-runway-filter-input"
                  />
                </div>
              </div>

              {/* Date Filter */}
              <div className="drone-runway-filter-field">
                <div
                  className={`drone-runway-input-wrapper drone-runway-date-wrapper ${dateInputFocused ? "drone-runway-date-focused" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    const dateInput = e.currentTarget.querySelector(".drone-runway-date-input");
                    if (dateInput && typeof dateInput.showPicker === "function") {
                      try {
                        dateInput.showPicker();
                      } catch (err) {
                        console.warn("Failed to open date picker using showPicker()", err);
                      }
                    }
                  }}
                >
                  <input
                    type="date"
                    value={filterDate}
                    onFocus={() => setDateInputFocused(true)}
                    onBlur={() => setDateInputFocused(false)}
                    onChange={(e) => {
                      setFilterDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof e.target.showPicker === "function") {
                        e.preventDefault();
                        try {
                          e.target.showPicker();
                        } catch (err) {
                          console.warn("Failed to open date picker using showPicker()", err);
                        }
                      }
                    }}
                    className="drone-runway-filter-input drone-runway-date-input"
                    aria-label="Filter by date in DD/MM/YYYY format"
                  />
                  <span className={`drone-runway-date-display ${filterDate ? "has-value" : ""}`}>
                    {filterDate ? formatDisplayDate(filterDate) : "DD/MM/YYYY"}
                  </span>
                  <span className="drone-runway-date-icon">
                    <TablerCalender size={15} color="currentColor" />
                  </span>
                </div>
              </div>

              {/* Severity Filter */}
              <div className="drone-runway-filter-field">
                <CustomSelect
                  value={severityFilter}
                  onChange={(val) => {
                    setSeverityFilter(val);
                    setCurrentPage(1);
                  }}
                  options={SEVERITY_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                />
              </div>

              {/* Status Filter */}
              <div className="drone-runway-filter-field">
                <CustomSelect
                  value={statusFilter}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(1);
                  }}
                  options={DRONE_RUNWAY_STATUS_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                />
              </div>

            </div>

            <button className="drone-runway-reset-btn" onClick={handleReset}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              {t("button.reset")}
            </button>
          </div>

          {/* Table Container */}
          <div className={`drone-runway-table-container ${!loading && paginatedAlerts.length === 0 ? "drone-runway-empty-container" : ""}`}>
            {loading ? (
              <LoadingSpinner message={t("loading.runwayAlerts")} />
            ) : paginatedAlerts.length === 0 ? (
              <div className="drone-runway-no-records">
                <h2>{t("label.noDataFound")}</h2>
              </div>
            ) : (
              <table className="drone-runway-table">
                <thead>
                  <tr>
                    <th className="drone-runway-th-first">{t("label.alertId")}</th>
                    <th>{t("label.timestampCol")}</th>
                    <th>{t("label.severity")}</th>
                    <th>{t("label.message")}</th>
                    <th className="drone-runway-th-last">{t("label.statusLabel")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAlerts.map((alert) => (
                    <tr key={alert.alertId || alert.id}>
                      <td className="drone-runway-td-first">
                        <Link
                          to={`/foreign-object-alerts/${encodeURIComponent(alert.alertId || alert.id)}`}
                          className="drone-runway-link"
                        >
                          {truncateId(alert.alertId || alert.id)}
                        </Link>
                      </td>
                      <td>{formatTime(alert.timestamp)}</td>
                      <td>
                        <span className={`drone-runway-badge badge-severity-${(alert.severity || "").toLowerCase()}`}>
                          {formatBadgeLabel(alert.severity)}
                        </span>
                      </td>
                      <td className="drone-runway-message-cell" title={alert.message}>
                        {alert.message}
                      </td>
                      <td className="drone-runway-td-last">
                        <span className={`drone-runway-badge badge-status-${(alert.status || "").toLowerCase()}`}>
                          {formatBadgeLabel(alert.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && filteredAlerts.length > 0 && (
            <div className="drone-runway-pagination">
              <div className="drone-runway-pagination-left">
                <span className="drone-runway-pagination-label">{t("label.rows")}</span>
                <CustomSelect
                  value={rowsPerPage}
                  onChange={(val) => {
                    setRowsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={ROWS_PER_PAGE_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                  className="alerts-rows-custom-select"
                />
                <span className="drone-runway-pagination-info">
                  {`${(currentPage - 1) * rowsPerPage + 1} - ${Math.min(currentPage * rowsPerPage, filteredAlerts.length)} of ${filteredAlerts.length}`}
                </span>
              </div>
              <div className="drone-runway-pagination-right">
                <button
                  className="drone-runway-page-arrow"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous Page"
                >
                  <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                    <path
                      d="M7.5 1L1.5 7L7.5 13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <button
                  className="drone-runway-page-arrow"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next Page"
                >
                  <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                    <path
                      d="M1.5 1L7.5 7L1.5 13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

export default DroneRunway;
